/* A single guess row. Walks the original sentence char-by-char so spacing
   and punctuation render in their canonical positions; letter slots come
   from the supplied buffer. */

import {
  buildRevealSlots,
  changedLetterIndices,
  prefersReducedMotion,
  revealDelayFor,
} from "../effects/reveal";
import type { PuzzleAnalysis } from "../game/puzzle";
import type { Mark } from "../game/scoring";
import { Cell } from "./Cell";

type Props = {
  analysis: PuzzleAnalysis;
  /** Length = analysis.letterCount. null cells render blank. */
  letters: readonly (string | null)[];
  /** Same length as `letters`, optional. */
  marks?: readonly Mark[] | undefined;
  /** Letter index in [0, letterCount) to highlight as cursor pair, or null. */
  cursorLetterIndex?: number | null;
  /** Render as a hint-paid row: empty, struck-through, dimmed. */
  paid?: boolean;
  /** When true, this is the just-submitted row — drive the outside-in
      flip-reveal. Pairs whose letters changed since the previous guess
      animate with a horizontal-axis flip; pairs that carried forward
      unchanged (typically green-locked positions) snap to their mark
      immediately and don't consume a stagger slot. Reduced-motion users
      skip the flip entirely. */
  revealing?: boolean;
  /** Previous submitted guess, used to detect which positions the player
      changed — only those flip. null on the very first guess (every
      position is treated as changed). */
  previousLetters?: readonly (string | null)[] | null;
};

export function Row({
  analysis,
  letters,
  marks,
  cursorLetterIndex = null,
  paid = false,
  revealing = false,
  previousLetters = null,
}: Props) {
  const { sentence, letterIndices, pivotLetterIndices, letterCount } = analysis;
  const mirrorOfCursor =
    cursorLetterIndex == null ? null : letterCount - 1 - cursorLetterIndex;
  const pivotSet = new Set(pivotLetterIndices);
  const animateReveal = revealing && !prefersReducedMotion();
  const slots = animateReveal
    ? buildRevealSlots(
        changedLetterIndices(letters, previousLetters),
        letterCount,
      )
    : null;

  // Build a quick lookup: char-index in sentence → letter-index, or undefined.
  const charToLetter = new Map<number, number>();
  for (let li = 0; li < letterIndices.length; li++) {
    charToLetter.set(letterIndices[li]!, li);
  }

  const out: preact.JSX.Element[] = [];
  for (let i = 0; i < sentence.length; i++) {
    const ch = sentence[i]!;
    const li = charToLetter.get(i);
    if (li !== undefined) {
      const isActive = li === cursorLetterIndex || li === mirrorOfCursor;
      const isPivot = pivotSet.has(li);
      // Pivot tick shows on every unsubmitted row (no marks). Cursor highlight
      // visually wins when both apply at the same cell.
      const showPivot = isPivot && !marks;
      const delay = slots
        ? revealDelayFor(li, letterCount, slots)
        : null;
      out.push(
        <Cell
          key={`c-${i}`}
          letter={letters[li] ?? null}
          mark={marks?.[li]}
          active={isActive}
          pivot={showPivot}
          revealDelayMs={delay ?? undefined}
        />,
      );
    } else if (ch === " ") {
      out.push(<span key={`s-${i}`} class="sep sep--space" />);
    } else {
      out.push(
        <span key={`p-${i}`} class="sep sep--punct">
          {ch}
        </span>,
      );
    }
  }

  // The row containing the cursor lifts above sibling rows so the
  // cell--active outline (which projects 2px into the gutter) isn't
  // painted over by the next row's cells when --cell-gap is small —
  // each row is its own stacking context (perspective: 800px), so a
  // z-index on the active cell alone can't escape its row.
  const cls = [
    "row",
    paid ? "row--paid" : "",
    cursorLetterIndex != null ? "row--active" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div class={cls} aria-label={paid ? "Hint-paid row" : undefined}>
      {out}
    </div>
  );
}
